import React from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ArrowLeft, Brain, Download, AlertTriangle, FileOutput
} from 'lucide-react';

const STATUS_COLORS = {
  requested: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  draft: 'bg-slate-100 text-slate-600',
  submitted: 'bg-cyan-100 text-cyan-700',
  returned: 'bg-red-100 text-red-700',
  resubmitted: 'bg-purple-100 text-purple-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  locked: 'bg-slate-800 text-white',
  overdue: 'bg-red-200 text-red-800',
};

export default function CombinedReportView({ combinedReport, onBack, onRunCycleAI, loading }) {
  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="h-4 w-4" /></Button>
            <h1 className="text-lg sm:text-xl font-bold text-slate-900">Combined Report Pack</h1>
            <Badge className="bg-cyan-100 text-cyan-700 text-xs hidden sm:inline-flex">{combinedReport.period_start} — {combinedReport.period_end}</Badge>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => onRunCycleAI(combinedReport.cycle_id)} disabled={loading}>
              <Brain className="h-4 w-4 mr-1" /> AI Analysis
            </Button>
            <Button variant="outline" size="sm" onClick={() => {
              const link = document.createElement('a');
              link.href = `${process.env.REACT_APP_BACKEND_URL}/api/periodic-reports/combined/${combinedReport.cycle_id}/pdf`;
              link.download = 'Combined_Report_Pack.pdf';
              link.click();
            }} data-testid="download-combined-pdf-btn">
              <Download className="h-4 w-4 mr-1" /> Download PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.print()}>
              <FileOutput className="h-4 w-4 mr-1" /> Print
            </Button>
          </div>
        </div>

        {/* Submission Register */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Submission Register</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full text-xs">
              <thead className="bg-slate-50"><tr>
                <th className="text-left px-3 py-2">Company</th>
                <th className="text-left px-3 py-2">Discipline</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Submitted</th>
                <th className="text-left px-3 py-2">Attachments</th>
              </tr></thead>
              <tbody className="divide-y">{combinedReport.submission_register?.map((s, i) => (
                <tr key={i} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-medium">{s.company}</td>
                  <td className="px-3 py-2">{s.discipline}</td>
                  <td className="px-3 py-2"><Badge className="text-[10px] bg-slate-100">{s.type}</Badge></td>
                  <td className="px-3 py-2"><Badge className={`text-[10px] ${STATUS_COLORS[s.status] || ''}`}>{s.status}</Badge></td>
                  <td className="px-3 py-2">{s.submitted_at ? new Date(s.submitted_at).toLocaleDateString() : '-'}</td>
                  <td className="px-3 py-2">{s.attachments}</td>
                </tr>
              ))}</tbody>
            </table>
          </CardContent>
        </Card>

        {/* AI Analysis */}
        {combinedReport.ai_analysis && (
          <Card className="border-purple-200" data-testid="combined-ai-analysis">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Brain className="h-4 w-4 text-purple-600" /> AI Executive Analysis</CardTitle></CardHeader>
            <CardContent className="space-y-4 text-sm">
              {combinedReport.ai_analysis.executive_summary && <div className="bg-purple-50 rounded-lg p-3 border border-purple-100"><p className="font-semibold text-purple-800 mb-1">Executive Summary</p><p className="text-slate-700">{combinedReport.ai_analysis.executive_summary}</p></div>}
              {combinedReport.ai_analysis.top_urgent_actions?.length > 0 && (
                <div><p className="font-semibold text-red-700 mb-1">Top Urgent Actions</p>
                  <ul className="list-disc pl-5 text-slate-600 space-y-1">{combinedReport.ai_analysis.top_urgent_actions.map((a, i) => <li key={i}>{typeof a === 'string' ? a : a.action || JSON.stringify(a)}</li>)}</ul>
                </div>
              )}
              {combinedReport.ai_analysis.health_assessment && <div className="bg-slate-50 rounded-lg p-3"><p className="font-semibold text-slate-700 mb-1">Project Health Assessment</p><p className="text-slate-600">{typeof combinedReport.ai_analysis.health_assessment === 'string' ? combinedReport.ai_analysis.health_assessment : JSON.stringify(combinedReport.ai_analysis.health_assessment)}</p></div>}
              {combinedReport.ai_analysis.common_themes && <div><p className="font-semibold text-slate-700 mb-1">Common Themes</p><p className="text-slate-600 text-xs">{typeof combinedReport.ai_analysis.common_themes === 'string' ? combinedReport.ai_analysis.common_themes : JSON.stringify(combinedReport.ai_analysis.common_themes)}</p></div>}
              {combinedReport.ai_analysis.contradictions && <div><p className="font-semibold text-amber-700 mb-1">Contradictions Between Parties</p><p className="text-slate-600 text-xs">{typeof combinedReport.ai_analysis.contradictions === 'string' ? combinedReport.ai_analysis.contradictions : JSON.stringify(combinedReport.ai_analysis.contradictions)}</p></div>}
              {combinedReport.ai_analysis.coordination_issues && <div><p className="font-semibold text-amber-700 mb-1">Coordination Issues</p><p className="text-slate-600 text-xs">{typeof combinedReport.ai_analysis.coordination_issues === 'string' ? combinedReport.ai_analysis.coordination_issues : JSON.stringify(combinedReport.ai_analysis.coordination_issues)}</p></div>}
              {combinedReport.ai_analysis.program_risks && <div><p className="font-semibold text-red-700 mb-1">Program Risks</p><p className="text-slate-600 text-xs">{typeof combinedReport.ai_analysis.program_risks === 'string' ? combinedReport.ai_analysis.program_risks : JSON.stringify(combinedReport.ai_analysis.program_risks)}</p></div>}
              {combinedReport.ai_analysis.responsiveness && <div><p className="font-semibold text-cyan-700 mb-1">Responsiveness Assessment</p><p className="text-slate-600 text-xs">{typeof combinedReport.ai_analysis.responsiveness === 'string' ? combinedReport.ai_analysis.responsiveness : JSON.stringify(combinedReport.ai_analysis.responsiveness)}</p></div>}
            </CardContent>
          </Card>
        )}

        {/* Urgent Items */}
        {combinedReport.all_urgent_items?.length > 0 && (
          <Card className="border-red-200">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-600" /> Urgent Items ({combinedReport.all_urgent_items.length})</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">{combinedReport.all_urgent_items.map((u, i) => (
                <div key={i} className="bg-red-50 border border-red-100 rounded-lg p-3 text-sm">
                  <p className="font-medium text-red-800">{u.item}</p>
                  <p className="text-xs text-red-600 mt-1">From: {u.source_company} — Deadline: {u.deadline || 'N/A'}</p>
                </div>
              ))}</div>
            </CardContent>
          </Card>
        )}

        {/* Issues */}
        {combinedReport.all_issues?.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">All Reported Issues ({combinedReport.all_issues.length})</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">{combinedReport.all_issues.slice(0, 20).map((issue, i) => (
                <div key={i} className="border rounded-lg p-3 text-sm">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-slate-800">{issue.title}</p>
                    <Badge className={`text-[10px] ${issue.urgency === 'high' ? 'bg-red-100 text-red-700' : issue.urgency === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{issue.urgency}</Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{issue.description}</p>
                  <p className="text-[10px] text-slate-400 mt-1">From: {issue.source_company} | Category: {issue.category}</p>
                </div>
              ))}</div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
